import { RequireProjectAdmin } from '@front/components/auth/RequireProjectAdmin';
import { useAnalyzeCsv, useUploadCsv } from '@front/hook/Api/File';
import { useImportTableDefinition } from '@front/hook/Api/ImportTableDefinition';
import { useImportTask } from '@front/hook/Api/ImportTask';
import { type NavigateBackFallback, useNavigateBack } from '@front/hook/useNavigateBack';
import { Button, FileUploaderDragDrop, type FileUploaderItem, Loader, useToast } from '@plaidev/sour-react';
import { createFileRoute, type ErrorComponentProps, useNavigate } from '@tanstack/react-router';
import { useProject } from 'auth-lib-front';
import { useEffect, useMemo, useState } from 'react';
import { DomainV2 } from 'troppus-common/domain';
import type { CsvAnalysis } from 'troppus-common/shared';
import { importTableDefinitionLoadModeLabel } from '../importTableDefinitionLoadModeLabel';

import ImportTableDefinition = DomainV2.Import.ImportTableDefinition;
import ImportTableDefinitionRevision = DomainV2.Import.ImportTableDefinitionRevision;
import ImportTask = DomainV2.Import.ImportTask;

export const Route = createFileRoute('/table-definitions/$id_/upload')({
  component: () => (
    <RequireProjectAdmin>
      <TableDefinitionUploadPage />
    </RequireProjectAdmin>
  ),
  errorComponent: TableDefinitionUploadError,
});

function TableDefinitionUploadError({ error }: ErrorComponentProps) {
  const message = error instanceof Error ? error.message : 'データの取得に失敗しました';
  return <div className="rounded-lg border border-negative bg-negative-light p-4 text-sm text-negative">{message}</div>;
}

function buildRevisionCandidateFromAnalysis(
  analysis: CsvAnalysis,
  lastRevision: ImportTableDefinitionRevision,
): Pick<ImportTableDefinitionRevision.Create.Props, 'schemaJson' | 'uniqueKeys' | 'loadMode' | 'importMethod'> {
  const currentColumns = new Map(lastRevision.schemaJson.columns.map((col) => [col.name, col]));

  return {
    schemaJson: {
      hasHeaderRow: analysis.hasHeaderRow,
      encoding: analysis.detectedEncoding,
      delimiter: analysis.detectedDelimiter,
      columns: analysis.columns.map((col) => {
        const current = currentColumns.get(col.name);
        if (current) {
          return {
            name: current.name,
            type: current.type,
            required: current.required,
            description: current.description,
            destination: current.destination,
            transformRules: [...current.transformRules],
            ...(current.multiValued ? { multiValued: true } : {}),
          };
        }
        return {
          name: col.name,
          type: col.inferredType,
          required: false,
          description: undefined,
          destination: col.inferredDestination,
          transformRules: [...col.inferredTransformRules],
        };
      }),
    },
    uniqueKeys: lastRevision.uniqueKeys,
    loadMode: lastRevision.loadMode,
    importMethod: lastRevision.インポート経路,
  };
}

/**
 * @feature CSVアップロード（取り込み）
 * @capabilities CSVファイル選択, 取り込みタスク起動
 * @behavior CSVをドラッグ＆ドロップで選択→「取り込み開始」でアップロード・LLM解析・リビジョン互換性チェックを行い、差分があれば新しいリビジョンを作成して取り込みタスクを起動しタスク詳細へ遷移。互換性が無い場合はその場でエラー表示、未完了タスクがある場合はブロックして既存タスクへ誘導。プロジェクト管理者専用
 * @airule IMPORTANT: Update this JSDoc when modifying this component's functionality
 */
function TableDefinitionUploadPage() {
  const navigate = useNavigate();
  const goBackWithFallback = useNavigateBack();
  const toast = useToast();
  const { project } = useProject();
  const { id: idParam } = Route.useParams();
  const tableDefinitionId = useMemo(() => ImportTableDefinition.Id.FromString(idParam), [idParam]);

  const { data, isPending } = useImportTableDefinition.Get({ projectId: project.id, id: tableDefinitionId });
  const { data: importTasks = [], isPending: importTasksPending } = useImportTask.List(project.id, {
    importTableDefinitionId: tableDefinitionId,
  });
  const uploadMutation = useUploadCsv();
  const analyzeMutation = useAnalyzeCsv();
  const checkRevisionCompatibilityMutation = useImportTableDefinition.CheckRevisionCompatibility();
  const createRevisionMutation = useImportTableDefinition.CreateRevision();
  const startImportMutation = useImportTask.Start();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadItems, setUploadItems] = useState<FileUploaderItem[]>([]);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);

  const incompleteImportTasks = useMemo(
    () =>
      importTasks
        .filter((t) => t.status === 'Requested' || t.status === 'Started')
        .sort((a, b) => b.createdAt.valueOf() - a.createdAt.valueOf()),
    [importTasks],
  );
  const hasIncompleteImportTask = incompleteImportTasks.length > 0;

  useEffect(() => {
    if (hasIncompleteImportTask) {
      setSelectedFile(null);
      setUploadItems([]);
    }
  }, [hasIncompleteImportTask]);

  const navigateBackFromUpload = () =>
    goBackWithFallback({
      to: '/table-definitions/$id',
      params: { id: idParam },
    } as unknown as NavigateBackFallback);
  const navigateToIncompleteTask = (taskId: ImportTask.Id) =>
    void navigate({
      to: '/tasks/$id',
      params: { id: taskId },
    });

  const handleSelectFiles = (files: FileList) => {
    if (hasIncompleteImportTask) return;
    const file = files.item(0);
    if (!file) return;
    const itemId = `${Date.now()}-${file.name}`;
    setSelectedFile(file);
    setUploadItems([{ id: itemId, name: file.name, state: 'uploaded' }]);
  };

  const handleDeleteUpload = (_fileId: string) => {
    setSelectedFile(null);
    setUploadItems([]);
  };

  const handleStartImport = async () => {
    if (hasIncompleteImportTask) {
      toast.error('未完了の取り込みタスクがあるため開始できません');
      return;
    }
    const file = selectedFile;
    if (!file) {
      toast.error('CSV ファイルを選択してください');
      return;
    }

    const currentItem = uploadItems[0];
    const itemId = currentItem?.id ?? `${Date.now()}-${file.name}`;
    setUploadItems([{ id: itemId, name: file.name, state: 'uploading' }]);
    setUploadProgress(0);

    try {
      const { fileId } = await uploadMutation.mutateAsync({
        projectId: project.id,
        file,
        onProgress: (ratio) => setUploadProgress(ratio),
      });
      setUploadItems([{ id: itemId, name: file.name, state: 'uploaded' }]);
      setUploadProgress(null);
      if (!data) throw new Error('テーブル定義が見つかりません');

      const { analysis } = await analyzeMutation.mutateAsync({ projectId: project.id, fileId });
      const candidate = buildRevisionCandidateFromAnalysis(analysis, data.lastRevision);
      const compatibility = await checkRevisionCompatibilityMutation.mutateAsync({
        projectId: project.id,
        id: tableDefinitionId,
        props: candidate,
      });
      if (compatibility.compatibilityType !== 'compatible') {
        throw new Error('CSV の列構成に後方互換性のない変更があります。新規テーブル定義として作成してください。');
      }

      const importTableDefinitionRevisionId = compatibility.hasDiff
        ? (
            await createRevisionMutation.mutateAsync({
              projectId: project.id,
              id: tableDefinitionId,
              props: candidate,
            })
          ).revision.id
        : data.definition.lastRevisionId;

      const task = await startImportMutation.mutateAsync({
        projectId: project.id,
        importTableDefinitionId: tableDefinitionId,
        importTableDefinitionRevisionId,
        fileId,
      });
      setSelectedFile(null);
      setUploadItems([]);
      setUploadProgress(null);
      toast.success(compatibility.hasDiff ? '新しい version を作成し、取り込みタスクを起動しました' : '取り込みタスクを起動しました');
      void navigate({
        to: '/tasks/$id',
        params: { id: task.id },
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : '取り込みの開始に失敗しました';
      setUploadItems([{ id: itemId, name: file.name, state: 'error' }]);
      setUploadProgress(null);
      toast.error(message);
      // createRevision / startImport がタイムアウト (AbortError) した場合のみサーバ側で
      // 副作用が残っている可能性があるため、テーブル定義詳細へ戻して状態を確認させる。
      // それ以外（GCS PUT 失敗 / 解析失敗 / 互換性なしの明示 throw 等）は副作用が無く、
      // 互換性エラーは本来この画面で対処してほしいガイダンスなので、その場に留める。
      if (err instanceof Error && err.name === 'AbortError') {
        navigateBackFromUpload();
      }
    }
  };

  if (isPending || importTasksPending) {
    return (
      <div className="flex justify-center py-16">
        <Loader />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="mx-auto flex max-w-3xl flex-col gap-4">
        <p className="text-sm text-muted-light">テーブル定義が見つかりません。</p>
        <Button variant="quiet" size="sm" onClick={() => goBackWithFallback({ to: '/table-definitions' })}>
          一覧へ戻る
        </Button>
      </div>
    );
  }

  const { definition, lastRevision } = data;
  const isImportPending =
    uploadMutation.isPending ||
    analyzeMutation.isPending ||
    checkRevisionCompatibilityMutation.isPending ||
    createRevisionMutation.isPending ||
    startImportMutation.isPending;
  const importBlocked = hasIncompleteImportTask;

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-8">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-col gap-1">
          <h1 className="text-xl font-semibold text-default">CSV アップロード</h1>
          <p className="text-sm text-muted-light">{definition.displayName}</p>
        </div>
        <Button type="button" variant="basic" size="sm" onClick={navigateBackFromUpload}>
          戻る
        </Button>
      </div>

      <section className="flex flex-col gap-3 rounded-lg border border-default p-4">
        <div className="flex flex-col gap-1">
          <h2 className="text-sm font-semibold text-default">CSV ファイル</h2>
          <p className="text-sm text-muted-light">
            ファイルをドラッグ＆ドロップして、このテーブル定義の取り込みタスクを起動します。取込モードは
            {importTableDefinitionLoadModeLabel(lastRevision.loadMode)}です。
          </p>
        </div>
        {importBlocked ? (
          <div className="flex flex-col gap-2 rounded-md border border-notice bg-notice-light p-3 text-sm text-default">
            <p>
              未完了の取り込みタスクがあります。完了してから続けられます（現在:{' '}
              {incompleteImportTasks[0]?.status === 'Requested' ? '受付済み' : '実行中'}）。
            </p>
            <Button
              type="button"
              variant="basic"
              size="sm"
              onClick={() => {
                const t = incompleteImportTasks[0];
                if (t) navigateToIncompleteTask(t.id);
              }}
            >
              取り込みタスクを開く
            </Button>
          </div>
        ) : null}
        <div className={importBlocked ? 'pointer-events-none opacity-50' : undefined}>
          <FileUploaderDragDrop accept=".csv,text/csv" list={uploadItems} uploadFile={handleSelectFiles} deleteFile={handleDeleteUpload} />
        </div>
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap gap-3">
            <Button
              type="button"
              variant="primary"
              size="sm"
              loading={isImportPending}
              disabled={!selectedFile || importBlocked}
              onClick={() => void handleStartImport()}
            >
              取り込み開始
            </Button>
            <Button type="button" variant="quiet" size="sm" onClick={navigateBackFromUpload}>
              キャンセル
            </Button>
          </div>
          {uploadMutation.isPending && uploadProgress !== null ? (
            <div className="flex flex-col gap-1">
              <div className="flex items-center justify-between text-xs text-muted-light">
                <span>{uploadProgress < 1 ? 'アップロード中…' : 'アップロード完了、解析を開始します…'}</span>
                <span>{Math.floor(uploadProgress * 100)}%</span>
              </div>
              <div
                className="h-2 overflow-hidden rounded-full bg-subtle"
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={Math.floor(uploadProgress * 100)}
                aria-label="アップロード進捗"
              >
                <div className="h-full bg-primary transition-all duration-300" style={{ width: `${Math.floor(uploadProgress * 100)}%` }} />
              </div>
            </div>
          ) : null}
        </div>
      </section>
    </div>
  );
}
